import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bed,
  Users,
  CheckCircle,
  XCircle,
  RefreshCw,
  Baby,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Select, type SelectOption } from "@/components/ui/Select";
import {
  Card,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { BedAssignDialog } from "./BedAssignDialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BedEntry {
  bed: {
    id: number;
    agencyId: number;
    siteId: number | null;
    roomName: string | null;
    bedName: string;
    isActive: boolean;
    notes: string | null;
  };
  occupant: {
    personId: number;
    firstName: string | null;
    lastName: string | null;
    intakeDate: string | null;
  } | null;
  cribs: {
    cribName: string;
    assignedDate: string;
  }[];
  status: "available" | "occupied" | "reserved" | "maintenance" | "inactive";
}

interface BedBoardResponse {
  data: {
    siteId: string | null;
    asOf: string;
    beds: BedEntry[];
    totalBeds: number;
    occupiedBeds: number;
    availableBeds: number;
    inactiveBeds: number;
  };
}

interface SiteOption {
  id: number;
  name: string;
  isActive: boolean;
}

interface SitesResponse {
  data: SiteOption[];
}

interface VacateResponse {
  data: Record<string, unknown>;
  message: string;
}

// ---------------------------------------------------------------------------
// Status colors
// ---------------------------------------------------------------------------

const statusColors: Record<string, string> = {
  available: "bg-green-100 border-green-300 text-green-800",
  occupied: "bg-blue-100 border-blue-300 text-blue-800",
  reserved: "bg-yellow-100 border-yellow-300 text-yellow-800",
  maintenance: "bg-red-100 border-red-300 text-red-800",
  inactive: "bg-gray-100 border-gray-300 text-gray-500",
};

const statusDotColors: Record<string, string> = {
  available: "bg-green-500",
  occupied: "bg-blue-500",
  reserved: "bg-yellow-500",
  maintenance: "bg-red-500",
  inactive: "bg-gray-400",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BedBoard() {
  const queryClient = useQueryClient();
  const [siteId, setSiteId] = useState("");
  const [selectedBed, setSelectedBed] = useState<BedEntry | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDetails, setShowDetails] = useState<BedEntry | null>(null);

  // Fetch service sites
  const { data: sitesData } = useQuery<SitesResponse>({
    queryKey: ["shelter-sites"],
    queryFn: () => api.get<SitesResponse>("/shelter/sites"),
  });

  const siteOptions: SelectOption[] = [
    { value: "", label: "All Sites" },
    ...(sitesData?.data ?? []).map((s) => ({
      value: String(s.id),
      label: s.name,
    })),
  ];

  // Fetch bed board
  const params: Record<string, string> = {};
  if (siteId) params.siteId = siteId;

  const {
    data: boardData,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useQuery<BedBoardResponse>({
    queryKey: ["bed-board", siteId],
    queryFn: () => api.get<BedBoardResponse>("/shelter/board", { params }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Vacate mutation
  const vacateMutation = useMutation({
    mutationFn: (bedId: number) =>
      api.post<VacateResponse>(`/shelter/beds/${bedId}/vacate`, {
        date: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bed-board"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-beds"] });
      setShowDetails(null);
    },
  });

  const board = boardData?.data;
  const beds = board?.beds ?? [];

  // Group beds by room
  const roomGroups = beds.reduce<Record<string, BedEntry[]>>((acc, entry) => {
    const room = entry.bed.roomName || "Unassigned";
    if (!acc[room]) acc[room] = [];
    acc[room].push(entry);
    return acc;
  }, {});

  const roomNames = Object.keys(roomGroups).sort((a, b) => {
    if (a === "Unassigned") return 1;
    if (b === "Unassigned") return -1;
    return a.localeCompare(b);
  });

  function handleBedClick(entry: BedEntry) {
    if (entry.status === "available") {
      setSelectedBed(entry);
      setShowAssignDialog(true);
    } else if (entry.status === "occupied") {
      setShowDetails(entry);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-56">
            <Select
              options={siteOptions}
              value={siteId}
              onChange={setSiteId}
              placeholder="All Sites"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-muted-foreground">
              Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {[
            { status: "available", label: "Available" },
            { status: "occupied", label: "Occupied" },
            { status: "reserved", label: "Reserved" },
            { status: "maintenance", label: "Maintenance" },
            { status: "inactive", label: "Inactive" },
          ].map(({ status, label }) => (
            <div key={status} className="flex items-center gap-1.5">
              <div
                className={cn("h-2.5 w-2.5 rounded-full", statusDotColors[status])}
              />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Occupancy stats bar */}
      {board && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            icon={Bed}
            label="Total Beds"
            value={board.totalBeds}
            color="text-foreground"
          />
          <StatCard
            icon={Users}
            label="Occupied"
            value={board.occupiedBeds}
            color="text-blue-600"
          />
          <StatCard
            icon={CheckCircle}
            label="Available"
            value={board.availableBeds}
            color="text-green-600"
          />
          <StatCard
            icon={XCircle}
            label="Inactive"
            value={board.inactiveBeds}
            color="text-gray-500"
          />
        </div>
      )}

      {/* Occupancy progress bar */}
      {board && board.totalBeds > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Occupancy</span>
            <span>
              {board.occupiedBeds} / {board.totalBeds - board.inactiveBeds} (
              {board.totalBeds - board.inactiveBeds > 0
                ? Math.round(
                    (board.occupiedBeds /
                      (board.totalBeds - board.inactiveBeds)) *
                      100
                  )
                : 0}
              %)
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-full rounded-full bg-blue-500 transition-all"
              style={{
                width: `${
                  board.totalBeds - board.inactiveBeds > 0
                    ? (board.occupiedBeds /
                        (board.totalBeds - board.inactiveBeds)) *
                      100
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-24 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && beds.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Bed className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No beds configured. Go to the Manage Beds tab to add beds.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Bed grid grouped by room */}
      {!isLoading &&
        roomNames.map((room) => (
          <div key={room} className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {room}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {roomGroups[room]?.map((entry) => (
                <button
                  key={entry.bed.id}
                  type="button"
                  onClick={() => handleBedClick(entry)}
                  className={cn(
                    "rounded-lg border-2 p-3 text-left transition-all hover:shadow-md",
                    statusColors[entry.status],
                    entry.status === "available" && "cursor-pointer hover:border-green-500",
                    entry.status === "occupied" && "cursor-pointer hover:border-blue-500",
                    entry.status === "inactive" && "cursor-not-allowed opacity-60"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-semibold truncate">
                      {entry.bed.bedName}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      {entry.cribs?.length > 0 && (
                        <Baby className="h-3.5 w-3.5 text-pink-500" title={`Crib: ${entry.cribs.map(c => c.cribName).join(', ')}`} />
                      )}
                      <div
                        className={cn(
                          "h-2.5 w-2.5 rounded-full",
                          statusDotColors[entry.status]
                        )}
                      />
                    </div>
                  </div>
                  {entry.occupant ? (
                    <div className="mt-1">
                      <p className="text-xs font-medium truncate">
                        {entry.occupant.firstName} {entry.occupant.lastName}
                      </p>
                      {entry.occupant.intakeDate && (
                        <p className="text-xs opacity-75">
                          Since{" "}
                          {new Date(
                            entry.occupant.intakeDate
                          ).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs capitalize opacity-75">
                      {entry.status}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}

      {/* Assign dialog */}
      {showAssignDialog && selectedBed && (
        <BedAssignDialog
          bed={{
            id: selectedBed.bed.id,
            bedName: selectedBed.bed.bedName,
            roomName: selectedBed.bed.roomName,
            status: selectedBed.status,
          }}
          onClose={() => {
            setShowAssignDialog(false);
            setSelectedBed(null);
          }}
          onAssigned={() => {
            setShowAssignDialog(false);
            setSelectedBed(null);
          }}
        />
      )}

      {/* Occupant details panel */}
      {showDetails && showDetails.occupant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg border bg-background shadow-lg">
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="text-lg font-semibold">Bed Details</h3>
              <button
                type="button"
                onClick={() => setShowDetails(null)}
                className="rounded-md p-1 hover:bg-accent"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <div>
                <p className="text-sm text-muted-foreground">Bed</p>
                <p className="font-medium">
                  {showDetails.bed.roomName
                    ? `${showDetails.bed.roomName} - `
                    : ""}
                  {showDetails.bed.bedName}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Occupant</p>
                <p className="font-medium">
                  {showDetails.occupant.firstName}{" "}
                  {showDetails.occupant.lastName}
                </p>
              </div>
              {showDetails.occupant.intakeDate && (
                <div>
                  <p className="text-sm text-muted-foreground">Since</p>
                  <p className="font-medium">
                    {new Date(
                      showDetails.occupant.intakeDate
                    ).toLocaleDateString()}
                  </p>
                </div>
              )}
              {showDetails.cribs?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Baby className="h-3.5 w-3.5 text-pink-500" />
                    Crib{showDetails.cribs.length > 1 ? "s" : ""} Attached
                  </p>
                  {showDetails.cribs.map((crib, i) => (
                    <div key={i} className="flex items-center gap-2 mt-1">
                      <p className="font-medium">{crib.cribName}</p>
                      <span className="text-xs text-muted-foreground">
                        since {new Date(crib.assignedDate).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {showDetails.bed.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{showDetails.bed.notes}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t p-4">
              <Button
                variant="outline"
                onClick={() => setShowDetails(null)}
              >
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={() => vacateMutation.mutate(showDetails.bed.id)}
                loading={vacateMutation.isPending}
              >
                Vacate Bed
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat Card sub-component
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <Icon className={cn("h-8 w-8", color)} />
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
